import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import * as Icons from '@ant-design/icons'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import type { APITypes } from '@qflow/api'

import { Select, LabeledValue } from 'portal/lib/sharedComponents/Select'
import {
  facilitiesLoad,
  facilitiesLoadContinuation
} from 'portal/store/wasteReferenceData/facilities/actions'

export interface EditWasteFacilitySelectorProps {
  onChange: () => void
  disabled?: boolean
  hidden?: boolean
  removalDate?: string | null
}

type FacilityOption = LabeledValue & {
  facility: APITypes.WasteFacility
}

export const EditWasteFacilitySelector: FC<EditWasteFacilitySelectorProps> = ({
  onChange,
  disabled = false,
  hidden = false,
  removalDate = null
}) => {
  const form = useFormContext()
  const dispatch = useDispatch()

  const [search, setSearch] = useState<string | null>(null)
  const {
    loading,
    hasNextPage,
    continuationToken,
    facilities,
    error
  }: {
    loading: boolean
    hasNextPage: boolean
    continuationToken: string
    facilities: APITypes.WasteFacility[]
    error: any
  } = useSelector((state: any) => state.facilities)

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  useEffect(() => {
    dispatch(facilitiesLoad(search, removalDate))
  }, [dispatch, removalDate, search])

  const options = useMemo(() => {
    if (!facilities?.length) {
      return []
    }

    return facilities.map<FacilityOption>(facility => {
      return {
        value: facility.permitId,
        label: `${facility.operator != null ? facility.operator : ''} (${facility.permitId})`,
        facility
      }
    })
  }, [facilities])

  const handleNextPage = useCallback(() => {
    dispatch(facilitiesLoadContinuation(search, removalDate, continuationToken))
  }, [dispatch, search, removalDate, continuationToken])

  const handleSelect = useCallback(
    (label, value) => {
      const option = value as FacilityOption

      form.setValue('data.facilityPermitId', option.facility.permitId, {
        shouldDirty: true
      })

      form.setValue(
        'data.facilityOperatorName',
        option.facility.operator != null ? option.facility.operator : '',
        {
          shouldDirty: true
        }
      )

      form.setValue(
        'data.facilitySiteAddress',
        option.facility.address.length > 0 ? option.facility.address[0] : '',
        {
          shouldDirty: true
        }
      )

      if (onChange) {
        onChange()
      }
    },
    [form, onChange]
  )

  if (hidden) {
    return null
  }

  return (
    <Select<FacilityOption>
      id="editWastefacilitieselector"
      showSearch
      loading={loading}
      placeholder={loading ? 'Fetching' : 'Select a Facility to apply'}
      popupMatchSelectWidth={true}
      filter={false}
      options={options}
      onSearch={setSearch}
      onBottomReached={hasNextPage ? handleNextPage : undefined}
      labelInValue
      onSelect={handleSelect}
      suffixIcon={<Icons.SearchOutlined />}
      disabled={disabled}
    />
  )
}
